/**
 * Pure URL→route mapping for incoming deep links. Capacitor's `appUrlOpen`
 * event fires when the OS hands the app a URL to open — a registered custom
 * URL scheme, or (once a human finishes the Associated Domains / App Links
 * native setup, see deep-links.ts) a Universal Link tapped outside the app,
 * such as the "View Schedule" button in a notification email (#960).
 *
 * Kept free of Capacitor imports so it can be unit-tested; the listener
 * that consumes it lives in deep-links.ts.
 */

// The web app's production host(s). Must stay in sync with the app origin
// used to build email links (PUBLIC_APP_URL /
// supabase/functions/_shared/email-template.ts BRAND.appUrl).
const ALLOWED_HOSTS = ['dashalign.com', 'app.dashalign.com'];

/**
 * Map a URL the OS handed the app to an in-app route, or null if it isn't
 * one we recognize. Only an https link on a known DashAlign host is
 * forwarded, so a malformed or spoofed URL can't navigate the app off-app.
 */
export function resolveDeepLinkTarget(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:') return null;
  if (!ALLOWED_HOSTS.includes(parsed.hostname)) return null;

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return path.startsWith('/') ? path : `/${path}`;
}
